/**
 * Persisted admin tokens (localStorage).
 *
 * Both tokens survive reloads + new tabs; the access token is hydrated into
 * the memory store on boot so a fresh tab works without a refresh round-trip.
 * Zero imports — safe to use from adminClient (module-cycle ban).
 *
 * Security note: localStorage is XSS-readable. The access token is short-lived
 * (staff TTL ≤15m); the refresh token already lived here and single-use-rotates
 * server-side with reuse rejection, so the marginal exposure is small.
 */
export const ADMIN_ACCESS_TOKEN_KEY = 'admin_access_token';
export const ADMIN_REFRESH_TOKEN_KEY = 'admin_refresh_token';

function safeGet(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Storage unavailable (private mode) — session simply won't survive reloads.
  }
}

export function readPersistedAccessToken(): string | null {
  return safeGet(ADMIN_ACCESS_TOKEN_KEY);
}

export function writePersistedAccessToken(token: string | null): void {
  safeSet(ADMIN_ACCESS_TOKEN_KEY, token);
}

export function readPersistedRefreshToken(): string | null {
  return safeGet(ADMIN_REFRESH_TOKEN_KEY);
}

export function writePersistedRefreshToken(token: string | null): void {
  safeSet(ADMIN_REFRESH_TOKEN_KEY, token);
}

/** Clears both persisted tokens (explicit logout only). */
export function clearPersistedTokens(): void {
  safeSet(ADMIN_ACCESS_TOKEN_KEY, null);
  safeSet(ADMIN_REFRESH_TOKEN_KEY, null);
}
